import { Formik } from "formik";
import { isEqual } from "lodash";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import {
  Accession,
  AccessionPATCH,
  AccessionWithCreatedCase,
  Case,
  PatientInsurance,
} from "pwapi";

import { ApiError } from "pwapi/core/ApiError";

import { LabList, LabStationList } from "custom-models/frontLoader";

import { useIsLoading } from "shared/hooks/requestStatusHooks";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import {
  newAccessionSchema,
  newAccessionWarningSchema,
} from "shared/utils/caseSchema";
import {
  formatTimestampForDateTime,
  prepareDatetimeStringForAPI,
} from "shared/utils/dates";
import { getValidationWarnings } from "shared/utils/validationUtils";

import {
  createAccession,
  getAccession,
  printAccession,
  updateAccession,
} from "app/actions/accessionActions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  useSelectedLab,
  useSelectedLabStation,
} from "hooks/useSelectedLabStation";
import AccessionForm, { blankInsurance } from "ui/accessioning/AccessionForm";
import { checkGuarantorAddress } from "ui/CaseForms/PatientGuarantorForm";
import ErrorToastBody from "ui/components/ErrorToastBody";
import Spinner from "ui/components/Spinner";

export const defaultAccession = (
  lab: LabList,
  station: LabStationList,
  object?: Partial<Accession>,
  accessionPrefixId?: number
) => {
  return {
    ...(lab.default_active_assignment_id && {
      active_assignment_id: lab.default_active_assignment_id,
    }),
    priority: Case.priority.NORMAL,
    ...object,
    lab_id: station.lab_id,
    lab_station_id: station.id,
    prefix_id: accessionPrefixId || (lab.default_prefix_id ?? 0), // awkward with types. this should be optional
  };
};

const AccessioningStationEdit = () => {
  const dispatch = useAppDispatch();
  const lab = useSelectedLab();
  const accessionPrefixId = lab.default_prefix_id;
  const defaultActiveAssignment = useAppSelector((state) =>
    lab.default_active_assignment_id
      ? state.users[lab.default_active_assignment_id]
      : undefined
  );
  const station = useSelectedLabStation();
  const navigate = useNavigate();
  const { state: navigationState } = useLocation();
  const accessionId = Number(useParams().accession_id || "");
  const accession = useAppSelector((state) =>
    state.accession?.id === accessionId ? state.accession : null
  );
  const autoPrint = !useAppSelector((state) =>
    selectFeatureFlag(state, "skipAutomaticBarcodePrinting")
  );
  const specimenBagBarcode = useAppSelector(
    (state) => state.specimenBag?.barcode?.specimen_bag_payload
  );
  const [warnings, setWarnings] = useState({});

  // Fetch Accession when there is an ID in the URL and it is not already loaded
  // TODO: `"new"` in the URL will yield `NaN` out of accessionId
  // A bit of smell, and something to consider in the future
  useEffect(() => {
    if (accessionId && (!accession?.id || accession.id !== accessionId)) {
      dispatch(
        getAccession({ id: accessionId, parentLookupLabStationId: station.id })
      ).catch((e) => {
        e.message &&
          toast.error(e.message, {
            position: "bottom-right",
            style: { fontSize: "1.5em" },
          });
        navigate("/cases");
      });
    }
  }, [accessionId, accession?.id]);

  const isCreating = useIsLoading(createAccession);
  const isPrinting = useIsLoading(printAccession, { id: accession?.id });
  const wasCreatingRef = useRef(isCreating);

  const initialValues = {
    priority: Accession.priority.NORMAL,
    active_assignment: accession?.active_assignment || defaultActiveAssignment,
    external_ezderm_order_id: "",
    external_patient_id: "",
    patient: {
      first_name: "",
      last_name: "",
      middle_initial: "",
      ssn: "",
      date_of_birth: undefined,
      sex: "",
    },
    address: {
      street: "",
      street2: "",
      city: "",
      state: "",
      zipcode: "",
      phone1: "",
      phone2: "",
    },
    guarantor: [],
    bill_tc: true,
    bill_pc: true,

    ...accession,

    collected_date:
      accession?.collected_date &&
      formatTimestampForDateTime(
        accession.collected_date,
        accession?.clinic?.location?.timezone
      ),
    collected_date_timezone: accession?.clinic?.location?.timezone,
    received_date:
      accession?.received_date &&
      formatTimestampForDateTime(
        accession.received_date,
        lab?.location?.timezone
      ),
    received_date_timezone: lab?.location?.timezone,
    specimens: accession?.specimens || [],
    insurance:
      accession?.insurance && accession?.insurance.length
        ? accession.insurance
        : [blankInsurance],
    billing_type:
      !accession?.billing_type ||
      (accession.billing_type as Accession.billing_type | "cash") === "cash"
        ? ""
        : accession.billing_type,
  };

  useEffect(() => {
    if (
      lab &&
      station &&
      Number.isNaN(accessionId) &&
      !isCreating &&
      !accession?.id &&
      wasCreatingRef.current === false
    ) {
      dispatch(
        createAccession({
          parentLookupLabStationId: station.id,
          requestBody: {
            ...defaultAccession(lab, station),
            received_date: DateTime.now().toISO(),
            ...(lab?.settings?.bag_tracking?.active && specimenBagBarcode
              ? { specimen_bag_barcode: specimenBagBarcode }
              : {}),
          },
        })
      ).then((accession: Accession) => {
        navigate(`/station/accessioning/${accession.id}/`, {
          state: navigationState,
          replace: true,
        });
      });
      wasCreatingRef.current = true;
    }
  }, [
    accessionId,
    lab,
    station,
    isCreating,
    accession?.id,
    specimenBagBarcode,
  ]);

  const onHandlePatchAccession = useCallback(
    (values: Partial<AccessionPATCH>, setBillerTypeDefaults?: boolean) => {
      if (!accession?.id) return;

      if (values.hasOwnProperty("guarantor") && values.guarantor) {
        // "Unwrap" Guarantor
        if (
          (Array.isArray(values.guarantor) && values.guarantor.length) ||
          (values.guarantor.hasOwnProperty("0") && values.guarantor[0])
        )
          values.guarantor = values.guarantor[0];

        const address = checkGuarantorAddress(values.guarantor.address);
        const guarantor = {
          ...values.guarantor,
          ssn: values.guarantor.ssn
            ? values.guarantor.ssn.replace(/\D/g, "")
            : values.guarantor.ssn,
          date_of_birth: values.guarantor.date_of_birth || undefined,
          address: address
            ? {
                street: address.street || "",
                street2: address.street2 || "",
                state: address.state || "",
                zipcode: address.zipcode || "",
                phone1: address.phone1 || "",
                phone2: address.phone2 || "",
                city: address.city || "",
              }
            : address,
        };

        values.guarantor = isEqual(guarantor, {}) ? null : [guarantor];
      }
      if (values.hasOwnProperty("address") && values.address) {
        values.address = checkGuarantorAddress(values.address);
      }

      if (values.hasOwnProperty("insurance") && values.insurance?.length) {
        values.insurance = values.insurance.filter((i: PatientInsurance) =>
          Object.values(i).some((i) => i)
        );
      }

      if (values.hasOwnProperty("patient") && values.patient.ssn) {
        values.patient.ssn = values.patient.ssn.replace(/\D/g, "");
      }

      return dispatch(
        updateAccession({
          id: accession.id,
          parentLookupLabStationId: station.id,
          requestBody: {
            ...values,
            lab_id: station.lab_id,
            lab_station_id: station.id,
            //TODO: this is likely going to be removed very soon. The logic is going to be moved to the backend.
            //@ts-ignore
            should_get_defaults: !!setBillerTypeDefaults,
            ...(accessionPrefixId ? { prefix_id: accessionPrefixId } : {}),

            ...(values.collected_date && {
              collected_date: prepareDatetimeStringForAPI(
                values.collected_date,
                accession?.clinic?.location?.timezone
              ),
            }),
            ...(values.received_date && {
              received_date: prepareDatetimeStringForAPI(
                values.received_date,
                lab?.location?.timezone
              ),
            }),
          },
        })
      );
    },
    [lab, accession?.id, accession?.clinic]
  );

  const onHandleSubmit = useCallback(
    (values) => {
      if (!accession?.id || !values || isPrinting) return;

      onHandlePatchAccession(values)?.then(() => {
        dispatch(
          printAccession({
            parentLookupLabStationId: station.id,
            id: accession?.id || 0,
          })
        )
          .then((response: AccessionWithCreatedCase) => {
            // Only auto-print specimens created by this request
            // Filter out specimens with the DIF Protocol
            let specimenIds = (response._meta?.created?.specimens || []).filter(
              (specimen_id) =>
                !response.created_case?.specimens
                  ?.find((s) => s.id === specimen_id)
                  ?.protocols?.includes("dif")
            );

            if (lab.settings?.print_two_specimen_labels_on_accession?.active) {
              specimenIds = specimenIds.flatMap((id) => [id, id]);
            }

            // Auto-print newly created documents as well
            const documentIds = response._meta?.created?.documents || [];
            if (autoPrint && specimenIds.length > 0) {
              if (lab.settings?.digital_pc?.active) {
                window.open(
                  `/print/${
                    response.created_case?.id
                  }/?documents=${documentIds.join(",")}`
                );
              } else {
                window.open(
                  `/print/${
                    response.created_case?.id
                  }/?specimens=${specimenIds.join(
                    ","
                  )}&documents=${documentIds.join(",")}`
                );
              }
            }
            navigate("/cases");
          })
          .catch((e: ApiError) => {
            e.message &&
              toast.error(
                (t) => (
                  <ErrorToastBody
                    e={e}
                    handleClose={() => toast.dismiss(t.id)}
                  />
                ),
                {
                  position: "bottom-right",
                  // Note: we eventually want className on <Toaster />
                  // ... but I didn't want to disturb other toasts for now
                  className: "Toast",
                }
              );
          });
      });
    },
    [accession?.id, isPrinting, station.id, onHandlePatchAccession]
  );

  return (
    <div className="Standard">
      <main>
        {accession?.id ? (
          <Formik
            initialValues={initialValues}
            onSubmit={onHandleSubmit}
            validate={(values: any) => {
              newAccessionWarningSchema
                .validate(values, {
                  abortEarly: false,
                  context: { address: values.address },
                })
                .then(() => {
                  setWarnings({});
                })
                .catch((err) => {
                  setWarnings(getValidationWarnings(err));
                });
            }}
            validateOnBlur={false}
            validationSchema={newAccessionSchema}
            validateOnMount={true}
          >
            <AccessionForm
              accession={accession}
              key={accessionId}
              onHandlePatchAccession={onHandlePatchAccession}
              warnings={warnings}
            />
          </Formik>
        ) : (
          <div>
            {accession?.id}
            <Spinner />
          </div>
        )}
      </main>
    </div>
  );
};

export default AccessioningStationEdit;
